import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ScrollText, Loader2, Filter, RefreshCw, User, Eye } from "lucide-react";
import { useAuth } from "@/lib/auth";

/**
 * Admin · Auditoría de acceso.
 * Muestra la traza de eventos `sensitive.read` y otras acciones administrativas
 * del tenant. Solo visible para tenant_admin / super_admin.
 */
export default function AdminAuditLogSection() {
  const { user } = useAuth();
  const isAdmin = user && ["tenant_admin", "super_admin"].includes(user.role);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ action: "", user_id: "", target: "" });

  const load = useCallback(async () => {
    if (!isAdmin) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: "150" });
      if (filters.action) params.set("action", filters.action);
      if (filters.user_id) params.set("user_id", filters.user_id);
      if (filters.target) params.set("target", filters.target);
      const r = await api.get(`/admin/audit-logs?${params.toString()}`);
      setData(r.data);
    } catch (e) {
      if (e?.response?.status !== 403) {
        toast.error(e?.response?.data?.detail || "No se pudo cargar la auditoría");
      }
    } finally { setLoading(false); }
  }, [isAdmin, filters]);

  useEffect(() => { load(); }, [load]);

  if (!isAdmin) return null;

  const readOnlyActions = ["sensitive.read"];
  const actionTone = (a) => {
    if (readOnlyActions.includes(a)) return "border-primary/40 text-primary bg-primary/5";
    if (a?.startsWith("admin.")) return "border-sev-medium/40 text-sev-medium bg-sev-medium/10";
    if (a?.startsWith("finding.") || a?.startsWith("client.")) return "border-sev-low/40 text-sev-low bg-sev-low/10";
    return "border-border text-muted-foreground";
  };

  return (
    <div className="border border-border p-5 bg-card/50" data-testid="admin-audit-section">
      <div className="flex items-start justify-between mb-4 gap-4">
        <div>
          <div className="overline flex items-center gap-2 text-primary">
            <ScrollText className="w-3 h-3" /> Auditoría · Acceso a datos sensibles
          </div>
          <h3 className="font-display text-xl font-medium mt-1">Trazabilidad de acceso</h3>
          <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
            Cada lectura de datos cifrados (clientes, dominios, IPs, hallazgos) queda
            registrada aquí junto con las acciones administrativas (login, cambios en MFA,
            actualización de hallazgos). Requerido para trazabilidad ENS/RGPD.
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          data-testid="audit-refresh-btn"
          className="border border-border px-3 py-1.5 text-xs font-mono tracking-widest uppercase hover:border-primary hover:text-primary inline-flex items-center gap-2"
        >
          {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
          Refrescar
        </button>
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap gap-2 mb-4 items-center">
        <Filter className="w-3 h-3 text-muted-foreground" />
        <input
          value={filters.action}
          onChange={(e) => setFilters(f => ({ ...f, action: e.target.value }))}
          placeholder="acción (p.ej. sensitive.read)"
          data-testid="audit-filter-action"
          className="bg-background border border-border px-2 py-1 text-xs font-mono w-48 focus:border-primary outline-none"
        />
        <input
          value={filters.target}
          onChange={(e) => setFilters(f => ({ ...f, target: e.target.value }))}
          placeholder="target (colección)"
          data-testid="audit-filter-target"
          className="bg-background border border-border px-2 py-1 text-xs font-mono w-40 focus:border-primary outline-none"
        />
        <input
          value={filters.user_id}
          onChange={(e) => setFilters(f => ({ ...f, user_id: e.target.value }))}
          placeholder="user_id (uuid)"
          data-testid="audit-filter-user"
          className="bg-background border border-border px-2 py-1 text-xs font-mono w-56 focus:border-primary outline-none"
        />
      </div>

      {/* Facets */}
      {data?.facets && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
          <div className="border border-border p-3">
            <div className="overline mb-2 flex items-center gap-1"><User className="w-3 h-3" /> Top usuarios</div>
            <div className="space-y-1" data-testid="audit-facet-users">
              {(data.facets.per_user || []).slice(0, 5).map(u => (
                <div key={u.user_id} className="flex justify-between text-xs font-mono">
                  <span className="truncate">{u.email || u.user_id}</span>
                  <span className="text-primary">{u.count}</span>
                </div>
              ))}
              {(!data.facets.per_user || !data.facets.per_user.length) && (
                <div className="text-xs text-muted-foreground">Sin eventos.</div>
              )}
            </div>
          </div>
          <div className="border border-border p-3">
            <div className="overline mb-2 flex items-center gap-1"><Eye className="w-3 h-3" /> Top acciones</div>
            <div className="space-y-1" data-testid="audit-facet-actions">
              {(data.facets.per_action || []).slice(0, 5).map(a => (
                <div key={a.action} className="flex justify-between text-xs font-mono">
                  <span className={`px-1.5 py-0.5 border text-[10px] ${actionTone(a.action)}`}>{a.action}</span>
                  <span className="text-primary">{a.count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Log table */}
      {loading && !data ? (
        <div className="flex items-center gap-2 text-xs font-mono text-muted-foreground">
          <Loader2 className="w-3 h-3 animate-spin" /> Cargando eventos…
        </div>
      ) : (
        <div className="overflow-x-auto max-h-[500px] overflow-y-auto border border-border">
          <table className="w-full text-xs font-mono" data-testid="audit-logs-table">
            <thead className="sticky top-0 bg-background z-10">
              <tr className="text-left overline text-muted-foreground border-b border-border">
                <th className="py-2 px-2">Cuándo</th>
                <th className="py-2 px-2">Quién</th>
                <th className="py-2 px-2">Acción</th>
                <th className="py-2 px-2">Objetivo</th>
                <th className="py-2 px-2">Detalle</th>
              </tr>
            </thead>
            <tbody>
              {(data?.logs || []).map((log, idx) => (
                <tr key={log.id || idx} className="border-b border-border/40 hover:bg-muted/20" data-testid={`audit-log-row-${idx}`}>
                  <td className="py-1.5 px-2 whitespace-nowrap text-muted-foreground">
                    {log.created_at ? new Date(log.created_at).toLocaleString() : "—"}
                  </td>
                  <td className="py-1.5 px-2 truncate max-w-[220px]" title={log.user_id}>
                    {log.user_email || log.user_id || "—"}
                  </td>
                  <td className="py-1.5 px-2">
                    <span className={`px-1.5 py-0.5 border text-[10px] uppercase tracking-widest ${actionTone(log.action)}`}>
                      {log.action}
                    </span>
                  </td>
                  <td className="py-1.5 px-2">{log.target || "—"}</td>
                  <td className="py-1.5 px-2 text-muted-foreground max-w-[420px] truncate">
                    {log.meta && (
                      <span title={JSON.stringify(log.meta)}>
                        {log.meta.doc_count !== undefined && `${log.meta.doc_count} docs · `}
                        {log.meta.operation && `${log.meta.operation} · `}
                        {log.meta.path && `${log.meta.path} · `}
                        {log.meta.role || ""}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
              {!data?.logs?.length && (
                <tr><td colSpan={5} className="py-4 text-center text-muted-foreground">Sin eventos registrados.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      {data?.total !== undefined && (
        <div className="text-[10px] font-mono text-muted-foreground mt-2 uppercase tracking-widest">
          Mostrando {data.total} eventos más recientes
        </div>
      )}
    </div>
  );
}
